import React, { useCallback } from 'react';
import { ActionIcon, Badge, Menu, Text, Tooltip } from '@mantine/core';
import { IconBrandStripe, IconDotsVertical, IconUser } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import CurrencyUtils from '@dr-pam/common-components/Utils/CurrencyUtils';
import DateTimeUtils, { DATE_FORMAT, DATE_TIME_FORMAT } from '@dr-pam/common-components/Utils/DateTimeUtils';
import NotificationUtils from '@dr-pam/common-components/Utils/NotificationUtils';
import type { RetryFulfillmentResult, UnfulfilledPaymentAuditRow } from '@dr-pam/common-components/Models/Payment';
import type { CrudTableColumn } from '../crud/CrudTable';
import CrudTable, { useCrudState } from '../crud/CrudTable';
import { openConfirmModal } from '../modals/ConfirmModal';
import { usePaymentService } from '../../services/PaymentService';
import { isFreeCheckoutPaymentIntentId } from '@dr-pam/common-components/Utils/FreeCheckoutUtils';

type UnfulfilledPayment = UnfulfilledPaymentAuditRow & { id: string };

function blockerLabel(blocker: RetryFulfillmentResult['replayBlocker']): string {
	switch (blocker) {
		case 'ACCESS_GRANTED_SINCE_PAYMENT':
			return 'The customer got newer access to this product after paying. Refund or adjust by hand.';
		case 'REFUNDED_IN_STRIPE':
			return 'The payment was refunded in Stripe.';
		case 'STRIPE_UNAVAILABLE':
			return 'Stripe is unavailable, so fulfilment could not be retried.';
		default:
			return 'This payment already has no unfulfilled subscription items.';
	}
}

function statusBadge(blocker: UnfulfilledPaymentAuditRow['replayBlocker']) {
	switch (blocker) {
		case 'ACCESS_GRANTED_SINCE_PAYMENT':
			return (
				<Tooltip label="A newer subscription to this product started after this payment (bought or granted by hand). Retry is disabled; refund or adjust manually.">
					<Badge color="green">Newer access exists</Badge>
				</Tooltip>
			);
		case 'REFUNDED_IN_STRIPE':
			return <Badge color="pink">Refunded in Stripe</Badge>;
		case 'STRIPE_UNAVAILABLE':
			return <Badge color="gray">Stripe unavailable</Badge>;
		default:
			return <Badge color="orange">Needs fulfilment</Badge>;
	}
}

export type UnfulfilledPaymentsListProps = {
	className?: string;
};

export default function UnfulfilledPaymentsList({ className }: UnfulfilledPaymentsListProps) {
	const paymentService = usePaymentService();
	const fetchUnfulfilled = useCallback(() => {
		const request = paymentService.getUnfulfilled();
		return {
			abort: request.abort,
			response: request.response.then((rows) => rows.map((row) => ({ ...row, id: row.paymentId }))),
		};
	}, [paymentService]);
	const crudState = useCrudState<UnfulfilledPayment, UnfulfilledPayment>(fetchUnfulfilled);

	const handleRetryClicked = (payment: UnfulfilledPayment) => {
		const customer = payment.userEmail;
		openConfirmModal({
			title: 'Retry fulfilment',
			children: <>Retry fulfilment for {customer}?</>,
			confirmText: 'Retry',
			onConfirm: async () => {
				try {
					const result = await paymentService.retryFulfillment(payment.paymentId).response;
					if (result.requeued) {
						NotificationUtils.showSuccess(`Fulfilment re-queued for ${customer}`, 'Retry queued');
					} else {
						NotificationUtils.showSuccess(blockerLabel(result.replayBlocker), 'Fulfilment not queued');
					}
				} catch (err) {
					NotificationUtils.showError(err as Error, 'Failed to retry fulfilment');
				} finally {
					crudState.refetch();
				}
			},
		});
	};

	const crudColumns = (payment: UnfulfilledPayment): CrudTableColumn[] => [
		{
			children: (
				<Tooltip label={DateTimeUtils.format(payment.paymentCreated, DATE_TIME_FORMAT)}>
					<Text size="sm">{DateTimeUtils.format(payment.paymentCreated, DATE_FORMAT)}</Text>
				</Tooltip>
			),
		},
		{
			children: (
				<Tooltip label={payment.userEmail}>
					<Text size="sm">{payment.userDisplayName || payment.userEmail}</Text>
				</Tooltip>
			),
		},
		{
			children: (
				<Tooltip label={payment.productName}>
					<Text size="sm">{payment.productShortName || payment.productName || '—'}</Text>
				</Tooltip>
			),
		},
		{
			children: (
				<Tooltip label={payment.currency}>
					<Text size="sm">{CurrencyUtils.format(payment.amountInCents, payment.currency)}</Text>
				</Tooltip>
			),
		},
		{ children: statusBadge(payment.replayBlocker) },
		{
			children: (
				<Menu shadow="md" width={220}>
					<Menu.Target>
						<ActionIcon color="dark" variant="subtle" aria-label={`Actions for ${payment.userEmail}`}>
							<IconDotsVertical />
						</ActionIcon>
					</Menu.Target>
					<Menu.Dropdown>
						<Menu.Item
							disabled={
								payment.replayBlocker === 'ACCESS_GRANTED_SINCE_PAYMENT' ||
								payment.replayBlocker === 'REFUNDED_IN_STRIPE'
							}
							onClick={() => handleRetryClicked(payment)}
						>
							Retry fulfilment
						</Menu.Item>
						{!isFreeCheckoutPaymentIntentId(payment.paymentIntentId) && (
							<Menu.Item
								component="a"
								href={`https://dashboard.stripe.com/payments/${payment.paymentIntentId}`}
								target="_blank"
								rel="noreferrer"
								leftSection={<IconBrandStripe size={16} />}
							>
								View in Stripe
							</Menu.Item>
						)}
						<Menu.Item
							component={Link}
							to={`/users/${payment.userId}`}
							leftSection={<IconUser size={16} />}
						>
							View user
						</Menu.Item>
					</Menu.Dropdown>
				</Menu>
			),
		},
	];

	return (
		<CrudTable
			className={className}
			items={crudState.items}
			headers={crudHeaders}
			columns={crudColumns}
			entityName="unfulfilled payment"
			isLoading={crudState.isFetching}
		/>
	);
}

const crudHeaders: CrudTableColumn[] = [
	{ children: 'Date paid' },
	{ children: 'Customer' },
	{ children: 'Product' },
	{ children: 'Amount' },
	{ children: 'Status' },
	{ children: '' },
];
